import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic multi-select autocomplete filter.
 *
 * Drop-in replacement for any single-`<select>` filter that picks
 * entities by id. State is an array of selected ids; the trigger
 * label collapses ("3 selected", or the single name when exactly one
 * is picked) and the popover hosts a search box + checkbox list of
 * options.
 *
 * Visual envelope matches `.filter-toggle-inactive` / `.filter-select`
 * (white background, 6px border-radius, gray border, hover/focus blue
 * ring) — kept consistent across all filter elements on the page.
 *
 * Callers supply:
 *   - `fetchOptions(search, includeInactive)`: returns up-to-PAGE_SIZE
 *     options matching the search; called whenever the popover is open
 *     and the debounced search changes. The component never paginates
 *     server-side — the search input is the discovery mechanism.
 *   - `fetchSelectedLabels(ids)`: resolves stable display names for
 *     ids already in `value` but not in the current `items` list
 *     (e.g. on mount, when the form was hydrated from URL). Optional
 *     — if absent, the id itself is shown until the user opens the
 *     popover and a search returns the matching option.
 *   - `allLabel`, optional `extraOptions` (e.g. "Active" / "No template").
 */

export interface MultiSelectOption {
  id: string;
  name: string;
}

interface ExtraOption {
  /** The id sent back to the parent in `value` when picked. */
  id: string;
  /** Display label inside the popover and the trigger. */
  label: string;
}

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  fetchOptions: (search: string, includeInactive: boolean) => Promise<MultiSelectOption[]>;
  fetchSelectedLabels?: (ids: string[]) => Promise<Record<string, string>>;
  allLabel: string;
  /** Pseudo-options that aren't entity ids (e.g. "0" for "No template"). */
  extraOptions?: ExtraOption[];
  className?: string;
  includeInactive?: boolean;
  /** data-testid hook for the trigger button (e2e specs). */
  testId?: string;
  /** Hint text in the search input. */
  searchPlaceholder?: string;
  /** Accessibility label exposed on the trigger button; falls back
   *  to allLabel. e2e specs use this via `page.getByLabel(...)`. */
  ariaLabel?: string;
}

const DEBOUNCE_MS = 250;

export const MultiSelectFilter: React.FC<Props> = ({
  value,
  onChange,
  fetchOptions,
  fetchSelectedLabels,
  allLabel,
  extraOptions = [],
  className,
  includeInactive = false,
  testId,
  searchPlaceholder,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [labelById, setLabelById] = useState<Record<string, string>>({});
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resolve display names for ids in value that we don't have in
  // labelById or extraOptions or items. Defensive — for hydrate paths.
  useEffect(() => {
    if (!fetchSelectedLabels) return;
    const known = new Set([
      ...Object.keys(labelById),
      ...extraOptions.map((o) => o.id),
      ...items.map((o) => o.id),
    ]);
    const missing = value.filter((v) => !known.has(v));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const fetched = await fetchSelectedLabels(missing);
        if (!cancelled) setLabelById((prev) => ({ ...prev, ...fetched }));
      } catch {
        /* leave the id as label */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, fetchSelectedLabels, labelById, extraOptions, items]);

  // Debounce search.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  // Fetch options whenever search / popover opens / includeInactive flips.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const next = await fetchOptions(debounced.trim(), includeInactive);
        if (!cancelled) {
          setItems(next);
          setLabelById((prev) => {
            const merged = { ...prev };
            for (const opt of next) merged[opt.id] = opt.name;
            return merged;
          });
        }
      } catch (err) {
        console.error('[MultiSelectFilter] fetch failed:', err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, debounced, includeInactive, fetchOptions]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePopover = document
        .querySelector('.multi-select-popover[data-open="true"]')
        ?.contains(target);
      if (!insideRoot && !insidePopover) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position popover (fixed) under the trigger, escaping any parent
  // overflow clipping.
  useEffect(() => {
    if (!open) {
      setPopoverPos(null);
      return;
    }
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPopoverPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 260),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Autofocus the search box on open.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const toggle = useCallback(
    (id: string) => {
      const set = new Set(value);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      onChange(Array.from(set));
    },
    [value, onChange],
  );

  const clearAll = useCallback(() => onChange([]), [onChange]);

  const triggerLabel =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? labelById[value[0]] ??
          extraOptions.find((o) => o.id === value[0])?.label ??
          value[0]
        : `${value.length} selected`;

  return (
    <div
      ref={rootRef}
      className={`multi-select-filter ${className || ''}`.trim()}
      style={{ position: 'relative' }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="filter-select multi-select-trigger"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || allLabel}
      >
        <span className="multi-select-trigger-label">{triggerLabel}</span>
        {value.length > 0 && (
          <span
            className="multi-select-clear"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            aria-label={'Clear'}
            title={'Clear'}
          >
            ×
          </span>
        )}
      </button>
      {open && popoverPos && (
        <div
          className="multi-select-popover"
          data-open="true"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            width: popoverPos.width,
            zIndex: 1000,
            background: 'var(--theme-bg-primary, #fff)',
            color: 'var(--theme-text-primary, #1f2937)',
            border: '1px solid var(--theme-border-primary, #d1d5db)',
            borderRadius: 6,
            maxHeight: 360,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
          role="listbox"
          aria-multiselectable="true"
        >
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder || allLabel}
            className="multi-select-search"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: 'none',
              borderBottom: '1px solid var(--theme-border-primary, #e5e7eb)',
              outline: 'none',
              boxSizing: 'border-box',
              background: 'inherit',
              color: 'inherit',
            }}
          />
          {value.length > 0 && (
            <div
              className="multi-select-option multi-select-clear-all"
              onClick={clearAll}
              style={{ padding: '0.45rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem', opacity: 0.8 }}
            >
              Clear selection
            </div>
          )}
          {extraOptions.map((opt) => (
            <Row
              key={opt.id}
              id={opt.id}
              label={opt.label}
              checked={value.includes(opt.id)}
              onToggle={toggle}
            />
          ))}
          {loading && (
            <div style={{ padding: '0.5rem 0.6rem', opacity: 0.6 }}>
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && debounced.trim() && (
            <div style={{ padding: '0.5rem 0.6rem', opacity: 0.6 }}>
              No results
            </div>
          )}
          {items.map((opt) => (
            <Row
              key={opt.id}
              id={opt.id}
              label={opt.name}
              checked={value.includes(opt.id)}
              onToggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onToggle: (id: string) => void;
}> = ({ id, label, checked, onToggle }) => (
  <label
    className="multi-select-option"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.45rem 0.6rem',
      cursor: 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={() => onToggle(id)}
      style={{ margin: 0 }}
    />
    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  </label>
);

export default MultiSelectFilter;
